/*
 * (c) Copyright, 2025, Medidata Solutions Worldwide, Inc. All rights reserved.
 */

import { useState } from 'react'
import { Form } from 'react-bootstrap'
import { useTranslation } from 'react-i18next'
import { Modal } from '../../../../components/Modal'
import { Task } from '../../../../services/api/types'
import { WorkflowStepType } from '../../../../constants/workflow'
import { useModal } from '../../../../contexts/ModalContext'
import TaskCompleteModal from '../complete/TaskCompleteModal'
import { TASK_ACTIONS } from '../../../../components/doc-preview/TaskComplete'
import TaskCancel from '../cancel/TaskCancel'

enum AfterRejectionAction {
  NOTHING = 'nothing',
  REVERT = 'revert',
  CANCEL = 'cancel'
}

interface AfterRejectionModalProps {
  origTask: Task
  targetTask: Task
  onClose?: () => void
}

/**
 * Modal shown after a task is rejected
 * Allows user to choose: do nothing, revert, or cancel the workflow
 * Migrated from: lego/page/tasks/after-rejection/Modal.jsx
 */
export default function AfterRejectionModal({ origTask, targetTask, onClose }: AfterRejectionModalProps) {
  const { t } = useTranslation()
  const { showModal } = useModal()
  const [action, setAction] = useState<AfterRejectionAction>(AfterRejectionAction.NOTHING)
  const [showCancelComponent, setShowCancelComponent] = useState(false)

  // Determine modal title based on workflow step type
  const getTitle = (): string => {
    const stepType = origTask.workflowStepType

    if (stepType === WorkflowStepType.DOWNLOAD_EOS) {
      return t('REJECTED_DOWNLOAD_EOS_H_TITLE')
    } else if (stepType === WorkflowStepType.ACKNOWLEDGE_EOS) {
      return t('REJECTED_ACKNOWLEDGE_EOS_H_TITLE')
    } else if (stepType === WorkflowStepType.REVISE_EDIT || stepType?.includes('REVISE_EDIT')) {
      return t('REJECTED_EDIT_H_TITLE')
    } else if (stepType === WorkflowStepType.REVIEW || stepType?.includes('REVIEW')) {
      return t('REJECTED_REVIEW_H_TITLE')
    } else if (stepType === WorkflowStepType.APPROVE || stepType?.includes('APPROVE')) {
      return t('REJECTED_APPROVAL_H_TITLE')
    }

    return ''
  }

  const handleAction = () => {
    if (action === AfterRejectionAction.REVERT) {
      // Show task complete modal for revert
      showModal(<TaskCompleteModal task={targetTask} type={TASK_ACTIONS.taskComplete} onClose={onClose} />)
    } else if (action === AfterRejectionAction.CANCEL) {
      // Close this modal and show cancel component

      showModal(<TaskCancel task={targetTask} />)
      // setShowCancelComponent(true)
    } else {
      // Do nothing - just close
      if (onClose) {
        onClose()
      }
    }
  }

  const handleClose = () => {
    if (onClose) {
      onClose()
    }
  }

  return (
    <>
      <Modal show={true} handleClose={handleClose} title={getTitle()} size="md">
        <Modal.Body>
          <h4 className="form-headline2 mt0 mb10">
            {t('REJECTED_TASK_ACTION_QUESTION', { workflowNo: (origTask as any).workflowFormattedId })}
          </h4>

          <Form.Check
            type="radio"
            id="e33314ff-1aeb-410a-bac8-300bc469596d-nothing"
            name="afterRevertAction"
            bsPrefix="classic mb10 w100p"
            label={t('REJECTED_TASK_ACTION_NO_ACTION')}
            checked={action === AfterRejectionAction.NOTHING}
            onChange={() => setAction(AfterRejectionAction.NOTHING)}
          />

          <Form.Check
            type="radio"
            id="e33314ff-1aeb-410a-bac8-300bc469596d-revert"
            name="afterRevertAction"
            label={t('REJECTED_TASK_ACTION_REVERT')}
            bsPrefix="classic mb10 w100p"
            checked={action === AfterRejectionAction.REVERT}
            onChange={() => setAction(AfterRejectionAction.REVERT)}
          />

          <Form.Check
            type="radio"
            id="e33314ff-1aeb-410a-bac8-300bc469596d-cancel"
            name="afterRevertAction"
            label={t('REJECTED_TASK_ACTION_CANCEL_CR')}
            bsPrefix="classic mb10 w100p"
            checked={action === AfterRejectionAction.CANCEL}
            onChange={() => setAction(AfterRejectionAction.CANCEL)}
          />
        </Modal.Body>
        <Modal.Footer>
          <Modal.Footer.Button onClick={handleAction} variant="primary">
            {t('OK')}
          </Modal.Footer.Button>
        </Modal.Footer>
      </Modal>
    </>
  )
}
